import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Skeleton } from "primereact/skeleton";
import { useEffect, useState } from "react";
import { getItem } from "../../../../../utils/localStorage";
import { makeRequest2 } from "../../../../../utils/makeRequest";
import { toastify } from "../../../../../utils/toast";
import { get_lease_templates } from "../../../../../utils/urls";

const LeaseTermsTab = ({
  formData,
  updateFormData,
  onNext,
  onPrevious,
  validation
}) => {
  const [leaseTemplates, setLeaseTemplates] = useState([]);

  useEffect(() => {
    fetchLeaseTemplates();
  }, []);

  const fetchLeaseTemplates = async () => {
    try {
      const facilityId = await getItem("selectedFacilityId");

      if (!facilityId) {
        toastify("No facility selected. Please select a facility first.", "error");
        return;
      }

      console.log("Fetching lease templates for facility:", facilityId);

      const response = await makeRequest2(`${get_lease_templates}/${facilityId}`, "GET");

      console.log("Lease templates response:", response);

      if (response.success) {
        console.log("Full lease templates response:", response);
        let templatesData = [];

        // Handle different possible response structures
        if (Array.isArray(response.data)) {
          templatesData = response.data;
        } else if (response.data?.templates) {
          templatesData = response.data.templates;
        } else if (response.data?.data) {
          templatesData = response.data.data;
        }

        setLeaseTemplates(templatesData);
        console.log("Lease templates loaded:", templatesData.length, templatesData);
      } else {
        console.error("Failed to fetch lease templates:", response);
        toastify("Failed to load lease templates", "error");
      }
    } catch (error) {
      console.error("Error fetching lease templates:", error);
      toastify("Failed to load lease templates", "error");
    }
  };

  // Guard against undefined formData
  if (!formData || !formData.leaseTerms) {
    return (
      <div className="surface-card p-6 border-round-lg shadow-2">
        <div className="flex align-items-center mb-4">
          <i className="ti ti-calendar text-2xl text-primary mr-3"></i>
          <h3 className="text-xl font-semibold text-900 m-0">Lease Terms</h3>
        </div>
        <div className="grid">
          <div className="col-12 md:col-4 mb-4">
            <div className="field">
              <label className="block text-900 font-medium mb-2">Start Date</label>
              <Skeleton width="100%" height="3rem"></Skeleton>
            </div>
          </div>
          <div className="col-12 md:col-4 mb-4">
            <div className="field">
              <label className="block text-900 font-medium mb-2">End Date</label>
              <Skeleton width="100%" height="3rem"></Skeleton>
            </div>
          </div>
          <div className="col-12 md:col-4 mb-4">
            <div className="field">
              <label className="block text-900 font-medium mb-2">Duration</label>
              <Skeleton width="100%" height="3rem"></Skeleton>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { leaseTerms } = formData;

  const calculateDuration = (start, end) => {
    if (!start || !end) return "";
    const startDate = new Date(start);
    const endDate = new Date(end);

    // Calculate the difference in months more accurately
    let monthDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 +
      (endDate.getMonth() - startDate.getMonth());

    // Adjust for day differences
    if (endDate.getDate() >= startDate.getDate()) {
      // If end day is same or later in the month, keep the month count as is
    } else {
      // If end day is earlier in the month, subtract one month
      monthDiff -= 1;
    }

    return Math.max(0, monthDiff); // Ensure non-negative
  };

  const handleDateChange = (value, type) => {
    if (type === "start") {
      const updates = { startDate: value };
      if (leaseTerms.endDate) {
        updates.leaseDuration = calculateDuration(value, leaseTerms.endDate).toString();
      }
      updateFormData.updateLeaseTerms(updates);
    } else {
      const updates = { endDate: value };
      if (leaseTerms.startDate) {
        updates.leaseDuration = calculateDuration(leaseTerms.startDate, value).toString();
      }
      updateFormData.updateLeaseTerms(updates);
    }
  };


  const handleNext = () => {
    const validationResult = validation.validateLeaseTerms();

    if (!validationResult.isValid) {
      validationResult.errors.forEach(error => {
        toastify(error, "error");
      });
      return;
    }

    onNext();
  };

  const maxEndDate = leaseTerms.startDate ?
    new Date(new Date(leaseTerms.startDate).getFullYear() + 10, 11, 31) :
    null;

  return (
    <div className="row">
      {/* Start Date */}
      <div className="col-md-6 mb-4">
        <div className="form-group">
          <label className="form-label">
            Start Date <span className="text-danger">*</span>
          </label>
          <Calendar
            value={leaseTerms.startDate ? new Date(leaseTerms.startDate) : null}
            onChange={(e) => handleDateChange(e.value, "start")}
            dateFormat="dd/mm/yy"
            placeholder="Select start date"
            className="w-100"
            showIcon
            showButtonBar={false}
          />
          {leaseTerms.startDate && (
            <small className="text-muted d-block mt-1">
              Selected: {new Date(leaseTerms.startDate).toLocaleDateString()}
            </small>
          )}
        </div>
      </div>

      {/* End Date */}
      <div className="col-md-6 mb-4">
        <div className="form-group">
          <label className="form-label">
            End Date <span className="text-danger">*</span>
          </label>
          <Calendar
            value={leaseTerms.endDate ? new Date(leaseTerms.endDate) : null}
            onChange={(e) => handleDateChange(e.value, "end")}
            minDate={leaseTerms.startDate ? new Date(leaseTerms.startDate) : null}
            maxDate={maxEndDate}
            dateFormat="dd/mm/yy"
            placeholder="Select end date"
            className="w-100"
            showIcon
            showButtonBar={false}
          />
          {leaseTerms.endDate && (
            <small className="text-muted d-block mt-1">
              Selected: {new Date(leaseTerms.endDate).toLocaleDateString()}
            </small>
          )}
        </div>
      </div>

      {/* Duration (Auto-calculated) */}
      <div className="col-md-4 mb-3">
        <div className="form-group">
          <label htmlFor="duration">
            Duration (Months)
          </label>
          <div className="form-control bg-light d-flex align-items-center" style={{ height: "42px" }}>
            {leaseTerms.leaseDuration ? (
              <span>{leaseTerms.leaseDuration} months</span>
            ) : (
              <span className="text-muted">Auto-calculated from dates</span>
            )}
          </div>
          <small className="text-muted">
            Automatically calculated when both dates are selected
          </small>
        </div>
      </div>

      {/* Lease Template */}
      <div className="col-md-6 mb-3">
        <div className="form-group">
          <label htmlFor="template">Lease Template</label>
          <Dropdown
            id="template"
            value={leaseTerms.leaseTemplate}
            options={leaseTemplates.map(template => ({
              label: template.name,
              value: template._id
            }))}
            onChange={(e) => updateFormData.updateLeaseTerms({ leaseTemplate: e.value })}
            placeholder="Choose template (optional)"
            className="w-100"
            showClear
            filter
            emptyMessage="No templates available"
          />
        </div>
      </div>


      {/* Rent Escalations */}
      <div className="col-12 mb-3">
        <div className="card">
          <div className="card-body">
            <h6 className="card-title text-primary">Rent Escalations</h6>
            <p className="text-muted mb-3">
              Define scheduled rent increases over the lease period. Escalations can be either a percentage increase or a fixed amount.
            </p>

            {(!formData.escalations || formData.escalations.length === 0) ? (
              <div className="text-center py-4">
                <i className="ti ti-trending-up text-4xl text-muted mb-3 d-block"></i>
                <p className="text-muted mb-3">
                  No rent escalations defined. Add an escalation to schedule future rent increases.
                </p>
                <Button
                  label="Add Escalation"
                  icon="ti ti-plus"
                  className="p-button-outlined p-button-primary"
                  onClick={() => {
                    const newEscalation = {
                      effectiveDate: null,
                      type: 'percentage',
                      value: 0,
                      status: 'scheduled'
                    };
                    const currentEscalations = formData.escalations || [];
                    updateFormData.updateLeaseTerms({
                      escalations: [...currentEscalations, newEscalation]
                    });
                  }}
                />
              </div>
            ) : (
              <div>
                {formData.escalations.map((escalation, index) => (
                  <div key={index} className="border rounded p-3 mb-3">
                    <div className="row">
                      <div className="col-md-3">
                        <label className="form-label">Effective Date <span className="text-danger">*</span></label>
                        <Calendar
                          value={escalation.effectiveDate ? new Date(escalation.effectiveDate) : null}
                          onChange={(e) => {
                            const updatedEscalations = [...formData.escalations];
                            updatedEscalations[index].effectiveDate = e.value;
                            updateFormData.updateLeaseTerms({ escalations: updatedEscalations });
                          }}
                          dateFormat="dd/mm/yy"
                          placeholder="Select date"
                          className="w-100"
                          showIcon
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Type <span className="text-danger">*</span></label>
                        <Dropdown
                          value={escalation.type}
                          options={[
                            { label: "Percentage", value: "percentage" },
                            { label: "Fixed Amount", value: "fixed" }
                          ]}
                          onChange={(e) => {
                            const updatedEscalations = [...formData.escalations];
                            updatedEscalations[index].type = e.value;
                            updateFormData.updateLeaseTerms({ escalations: updatedEscalations });
                          }}
                          className="w-100"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Value <span className="text-danger">*</span></label>
                        <InputNumber
                          value={escalation.value}
                          onValueChange={(e) => {
                            const updatedEscalations = [...formData.escalations];
                            updatedEscalations[index].value = e.value;
                            updateFormData.updateLeaseTerms({ escalations: updatedEscalations });
                          }}
                          suffix={escalation.type === 'percentage' ? '%' : ` ${(formData.basicInfo && formData.basicInfo.selectedCurrency && formData.basicInfo.selectedCurrency.currencySymbol) || ''}`}
                          className="w-100"
                          min={0}
                        />
                      </div>
                      <div className="col-md-3 d-flex align-items-end">
                        <Button
                          icon="ti ti-trash"
                          className="p-button-danger p-button-outlined w-100"
                          onClick={() => {
                            const updatedEscalations = formData.escalations.filter((_, i) => i !== index);
                            updateFormData.updateLeaseTerms({ escalations: updatedEscalations });
                          }}
                          label="Remove"
                        />
                      </div>
                    </div>
                  </div>
                ))}
                <Button
                  label="Add Another Escalation"
                  icon="ti ti-plus"
                  className="p-button-outlined p-button-primary"
                  onClick={() => {
                    const newEscalation = {
                      effectiveDate: null,
                      type: 'percentage',
                      value: 0,
                      status: 'scheduled'
                    };
                    const currentEscalations = formData.escalations || [];
                    updateFormData.updateLeaseTerms({
                      escalations: [...currentEscalations, newEscalation]
                    });
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="col-12 mt-4">
        <div className="d-flex justify-content-between">
          <Button
            label="Previous: Basic Info"
            icon="pi pi-arrow-left"
            onClick={onPrevious}
          />
          <Button
            label="Next: Financial Terms"
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={handleNext}
            disabled={!leaseTerms.startDate || !leaseTerms.endDate || !leaseTerms.leaseDuration}
          />
        </div>
      </div>
    </div>
  );
};

export default LeaseTermsTab;